/**
 * Money. See ADR-0002 and CLAUDE.md 3.1.
 *
 * Money is integer cents, always. Never a float, never a `number` of dollars.
 * Formatting to dollars happens only at the presentation edge.
 */
export const DEFAULT_CURRENCY = 'USD';
export function money(cents, currency = DEFAULT_CURRENCY) {
    return { cents, currency };
}
/**
 * Parse a human-entered dollar string into cents, exactly.
 *
 * Deliberately string-based: routing the value through `parseFloat` would reintroduce the
 * binary-floating-point error this whole rule exists to avoid.
 *
 * Accepts: "1234.56", "-1,234.56", "$1,234.56", "1234", ".5", "1234.5"
 * Rejects: anything with more than two decimal places, and anything unparseable.
 */
export function dollarsToCents(input) {
    const cleaned = input.trim().replace(/[$,\s]/g, '');
    const match = /^(-?)(\d*)(?:\.(\d{0,2}))?$/.exec(cleaned);
    if (!match) {
        throw new RangeError(`Not a valid dollar amount: ${JSON.stringify(input)}`);
    }
    const [, sign, whole, frac] = match;
    if ((whole ?? '') === '' && (frac ?? '') === '') {
        throw new RangeError(`Not a valid dollar amount: ${JSON.stringify(input)}`);
    }
    const wholeCents = BigInt(whole === '' || whole === undefined ? '0' : whole) * 100n;
    const fracCents = BigInt((frac ?? '').padEnd(2, '0') || '0');
    const total = wholeCents + fracCents;
    return sign === '-' ? -total : total;
}
/** Format cents for display. Presentation edge only. */
export function formatCents(cents, currency = DEFAULT_CURRENCY) {
    const negative = cents < 0n;
    const abs = negative ? -cents : cents;
    const whole = abs / 100n;
    const frac = abs % 100n;
    const wholeStr = whole.toLocaleString('en-US');
    const symbol = currency === 'USD' ? '$' : '';
    return `${negative ? '-' : ''}${symbol}${wholeStr}.${frac.toString().padStart(2, '0')}`;
}
/** Sum. Exact by construction. */
export function sumCents(values) {
    let total = 0n;
    for (const v of values)
        total += v;
    return total;
}
/**
 * Apply a rate (e.g. a tax or commission percentage) to a cent amount.
 *
 * Rounding is HALF_UP on the absolute value, so -0.5 rounds to -1 and 0.5 rounds to 1 —
 * symmetric, and the convention accountants expect. The caller must pass the rate as an
 * exact numerator/denominator pair rather than a float, so the rounding is the only
 * inexactness and it happens exactly once, here, where it can be tested.
 */
export function applyRate(cents, numerator, denominator) {
    if (denominator === 0n)
        throw new RangeError('applyRate: denominator must not be zero');
    const product = cents * numerator;
    const negative = product < 0n !== denominator < 0n;
    const absProduct = product < 0n ? -product : product;
    const absDenominator = denominator < 0n ? -denominator : denominator;
    const quotient = absProduct / absDenominator;
    const remainder = absProduct % absDenominator;
    const rounded = remainder * 2n >= absDenominator ? quotient + 1n : quotient;
    return negative ? -rounded : rounded;
}
export function serializeMoney(m) {
    return {
        cents: m.cents.toString(),
        currency: m.currency,
        formatted: formatCents(m.cents, m.currency),
    };
}
//# sourceMappingURL=money.js.map